import type {
  AnalyticsResponse,
  Deck,
  DeckDetail,
  DeckVersion,
  Format,
  GameDetail,
  GameSummary,
  LeaderboardResponse,
  MeResponse,
  Profile,
  Season,
  UploadGameRequest,
  UploadGameResponse,
} from "@pra/shared/contracts";
import type { DecklistDiff } from "@pra/shared/decklist";
import { authHeaders, setToken } from "./auth";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

const SCOPE_KEY = "pra.scope";

function readStoredScope(): string | undefined {
  try {
    return sessionStorage.getItem(SCOPE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

// Initialised synchronously so the first queries after a full page load are already scoped
// (the session provider re-applies it once it knows the user is an admin).
let scopeUserId: string | undefined = readStoredScope();
export function getScopeUserId(): string | undefined {
  return scopeUserId;
}

/** Admin-only: scope subsequent requests to another user (`?userId=`). */
export function setScopeUserId(userId: string | undefined): void {
  scopeUserId = userId;
}

export async function api<T>(
  method: string,
  path: string,
  body?: unknown,
  opts: { raw?: boolean; scoped?: boolean } = {},
): Promise<T> {
  const url = new URL(path, window.location.origin);
  if ((opts.scoped ?? true) && scopeUserId !== undefined && !url.searchParams.has("userId"))
    url.searchParams.set("userId", scopeUserId);
  const response = await fetch(url.pathname + url.search, {
    method,
    credentials: "include",
    headers: {
      ...(body === undefined ? {} : { "content-type": "application/json" }),
      ...authHeaders(),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (response.status === 401) setToken(undefined);
  if (response.status === 403 && scopeUserId !== undefined) {
    // A stale admin scope (or a non-admin session) — drop it so the page recovers on reload.
    scopeUserId = undefined;
    try {
      sessionStorage.removeItem(SCOPE_KEY);
    } catch {
      /* ignore */
    }
  }
  if (!response.ok) {
    let code = "error";
    let message = `Request failed (${response.status})`;
    try {
      const data = (await response.json()) as { error?: string; message?: string };
      code = data.error ?? code;
      message = data.message ?? message;
    } catch {
      /* non-json */
    }
    throw new ApiError(response.status, code, message);
  }
  if (opts.raw) return (await response.text()) as T;
  return (await response.json()) as T;
}

export const endpoints = {
  me: () => api<MeResponse>("GET", "/api/me", undefined, { scoped: false }),
  updateProfile: (tcglHandles: string[]) =>
    api<MeResponse>("PUT", "/api/me/profile", { tcglHandles }, { scoped: false }),
  uploadGame: (req: UploadGameRequest) =>
    api<UploadGameResponse>("POST", "/api/games", req, { scoped: false }),
  games: () => api<{ games: GameSummary[] }>("GET", "/api/games"),
  game: (id: string) => api<GameDetail>("GET", `/api/games/${id}`),
  rawLog: (id: string) => api<string>("GET", `/api/games/${id}/raw`, undefined, { raw: true }),
  setSide: (id: string, body: { mySide: number; deckId?: string; newDeckName?: string }) =>
    api<{ game: GameSummary }>("PUT", `/api/games/${id}/side`, body),
  attachDecklist: (id: string, side: "me" | "opponent", text: string) =>
    api<GameDetail>("PUT", `/api/games/${id}/decklists/${side}`, { text }),
  deleteGame: (id: string) => api<{ ok: true }>("DELETE", `/api/games/${id}`),
  decks: () => api<{ decks: Deck[] }>("GET", "/api/decks"),
  deck: (id: string) => api<DeckDetail & { games: GameSummary[] }>("GET", `/api/decks/${id}`),
  patchDeck: (id: string, body: { name?: string; archived?: boolean }) =>
    api<{ deck: Deck }>("PATCH", `/api/decks/${id}`, body),
  deckDiff: (id: string, from: number, to: number) =>
    api<{ from: DeckVersion; to: DeckVersion; diff: DecklistDiff }>(
      "GET",
      `/api/decks/${id}/diff?from=${from}&to=${to}`,
    ),
  analytics: (deckId?: string) =>
    api<AnalyticsResponse>("GET", deckId ? `/api/analytics?deckId=${deckId}` : "/api/analytics"),
  setGameSeason: (id: string, seasonId: string | null) =>
    api<{ game: GameSummary }>("PUT", `/api/games/${id}/season`, { seasonId }),
  leaderboard: (seasonId?: string) =>
    api<LeaderboardResponse>(
      "GET",
      seasonId ? `/api/leaderboard?seasonId=${seasonId}` : "/api/leaderboard",
      undefined,
      { scoped: false },
    ),
  seasons: () =>
    api<{ seasons: Season[]; formats: Format[] }>("GET", "/api/seasons", undefined, {
      scoped: false,
    }),
  patchSeason: (id: string, body: Record<string, string | null | undefined>) =>
    api<{ season: Season }>("PATCH", `/api/admin/seasons/${id}`, body, { scoped: false }),
  createFormat: (body: {
    name: string;
    description?: string;
    startsAt?: string;
    endsAt?: string;
  }) => api<{ format: Format }>("POST", "/api/admin/formats", body, { scoped: false }),
  updateFormat: (
    id: string,
    body: { name: string; description?: string; startsAt?: string; endsAt?: string },
  ) => api<{ format: Format }>("PATCH", `/api/admin/formats/${id}`, body, { scoped: false }),
  deleteFormat: (id: string) =>
    api<{ ok: true }>("DELETE", `/api/admin/formats/${id}`, undefined, { scoped: false }),
  adminUsers: () =>
    api<{ users: Profile[] }>("GET", "/api/admin/users", undefined, { scoped: false }),
  adminGames: () =>
    api<{ games: GameSummary[] }>("GET", "/api/admin/games", undefined, { scoped: false }),
  uploadCatalog: (catalog: unknown) =>
    api<{ ok: true; cards: number }>("PUT", "/api/admin/catalog", catalog, { scoped: false }),
};
